/* eslint-disable no-unsafe-optional-chaining */
import { Accordion, AccordionTab } from 'primereact/accordion';
import React, { useEffect, useState } from 'react';

import { ScorecardPayoutAgingActivity } from '../scorecardPayoutAgingActivity/ScorecardPayoutAgingActivity';
import {
    useGetScorecardEmployeePayoutAgingCountQuery,
    useGetScorecardPayoutAgingCountQuery,
} from '../scorecardPayoutAgingActivity/scorecardPayoutAgingActivitySlice';
import { ScorecardAgingGraph } from './ScorecardAgingGraph';
import './payoutAging.scss';

interface PayoutAgingProps {
    scorecardVersion: React.Dispatch<any>;
    activeIndexForTab: React.Dispatch<any>;
    scorecardId: number | string;
}

export const PayoutAging = ({ scorecardVersion, activeIndexForTab, scorecardId }: PayoutAgingProps) => {
    function subtractMonthsFromDate(
        currentDate: Date,
        monthsToSubtract: number
    ): { month: number; year: number } {
        const date = new Date(currentDate);
        date.setMonth(date.getMonth() - monthsToSubtract);
        const month = date.getMonth() + 1; // JavaScript months are 0-11
        const year = date.getFullYear();
        return { month, year };
    }

    const [scorcardGraphData, setScorecardGraphData] = useState<any[]>();
    const [employeeGraphData, setEmployeeGraphData] = useState<any[]>();

    const pastThreeMonths = subtractMonthsFromDate(new Date(), 3);
    const pastTwoMonths = subtractMonthsFromDate(new Date(), 2);
    const pastOneMonth = subtractMonthsFromDate(new Date(), 1);
    const currentMoth = subtractMonthsFromDate(new Date(), 0);

    const { data: scorecardPayoutAgingCount } = useGetScorecardPayoutAgingCountQuery(
        {
            year: currentMoth.year,
            month: currentMoth.month,
            scorecardId,
        },
        { skip: !currentMoth.year || !currentMoth.month }
    );

    const { data: employeePayoutAgingCount } = useGetScorecardEmployeePayoutAgingCountQuery(
        {
            year: currentMoth.year,
            month: currentMoth.month,
        },
        { skip: !currentMoth.year || !currentMoth.month }
    );

    useEffect(() => {
        const graphData: unknown[] = [];
        if (scorecardPayoutAgingCount?.data[0]) {
            const scorecardPayoutAgingCountData = scorecardPayoutAgingCount?.data[0];
            if (scorecardPayoutAgingCountData) {
                const arrayOfArrays = Object.entries(scorecardPayoutAgingCountData);
                arrayOfArrays.forEach((item) => {
                    graphData.push(item[1]);
                });
            }
        }
        if (graphData.length > 0) {
            setScorecardGraphData(graphData);
        }

        const graphDataEmp: unknown[] = [];
        if (employeePayoutAgingCount?.data[0]) {
            const employeePayoutAgingCountData = employeePayoutAgingCount?.data[0];
            if (employeePayoutAgingCountData) {
                const arrayOfArrays = Object.entries(employeePayoutAgingCountData);
                arrayOfArrays.forEach((item) => {
                    graphDataEmp.push(item[1]);
                });
            }
        }
        if (graphDataEmp.length > 0) {
            setEmployeeGraphData(graphDataEmp);
        }
    }, [scorecardPayoutAgingCount, employeePayoutAgingCount]);

    return (
        <div className="card  agingContainer mt-3 ms-0 me-0 bg-body  border-0">
            <div className="card bg-red d-flex flex-row justify-content-center align-items-center">
                <div className="w-50">
                    {scorcardGraphData && (
                        <ScorecardAgingGraph
                            graphData={scorcardGraphData || []}
                            labels={[
                                'Payout past 3+ Months',
                                'Payout past 2 months',
                                'Payout past 1 Month',
                                'Payout This Month',
                            ]}
                            metricName="Scorecards"
                        />
                    )}
                </div>

                <div className="w-50">
                    {employeeGraphData && (
                        <ScorecardAgingGraph
                            graphData={employeeGraphData || []}
                            labels={[
                                'Payout past 3+ Months',
                                'Payout past 2 months',
                                'Payout past 1 Month',
                                'Payout This Month',
                            ]}
                            metricName="Employees"
                        />
                    )}
                </div>
            </div>

            <Accordion className="mt-3">
                <AccordionTab
                    header={`Alert, Payout past due more than 3 Months -
                        ${scorecardPayoutAgingCount?.data[0]?.due_more_than_3month ?? 0}`}
                    headerClassName="custom-header custom-header-red"
                >
                    <ScorecardPayoutAgingActivity
                        inputmonth={pastThreeMonths.month}
                        inputyear={pastThreeMonths.year}
                        scorecardVersion={scorecardVersion}
                        activeIndexForTab={activeIndexForTab}
                        scorecardId={scorecardId}
                        isMonthsMoreThanOne
                    />
                </AccordionTab>
                <AccordionTab
                    header={`Alert, Payout past due 2 Months - 
                        ${scorecardPayoutAgingCount?.data[0]?.due_two_month ?? 0}`}
                    headerClassName="custom-header custom-header-orange"
                >
                    <ScorecardPayoutAgingActivity
                        inputmonth={pastTwoMonths.month}
                        inputyear={pastTwoMonths.year}
                        scorecardVersion={scorecardVersion}
                        activeIndexForTab={activeIndexForTab}
                        scorecardId={scorecardId}
                        isMonthsMoreThanOne={false}
                    />
                </AccordionTab>
                <AccordionTab
                    header={`Alert, Payout past due 1 Month - 
                        ${scorecardPayoutAgingCount?.data[0]?.due_one_month ?? 0}`}
                    headerClassName="custom-header custom-header-yellow"
                >
                    <ScorecardPayoutAgingActivity
                        inputmonth={pastOneMonth.month}
                        inputyear={pastOneMonth.year}
                        scorecardVersion={scorecardVersion}
                        activeIndexForTab={activeIndexForTab}
                        scorecardId={scorecardId}
                        isMonthsMoreThanOne={false}
                    />
                </AccordionTab>
                <AccordionTab
                    header={`Scorecard Payout Period is this month - 
                        ${scorecardPayoutAgingCount?.data[0]?.this_month ?? 0}`}
                    headerClassName="custom-header custom-header-blue-light"
                >
                    <ScorecardPayoutAgingActivity
                        inputmonth={currentMoth.month}
                        inputyear={currentMoth.year}
                        scorecardVersion={scorecardVersion}
                        activeIndexForTab={activeIndexForTab}
                        scorecardId={scorecardId}
                        isMonthsMoreThanOne={false}
                    />
                </AccordionTab>
            </Accordion>
        </div>
    );
};
